import { ChangeDetectionStrategy, Component, computed, effect, input, output, signal } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { NavigationEnd } from '@angular/router';
import { reloadHelper } from 'lib/helpers/reload-helper.class.js';
import { IButton } from 'lib/ui/buttons/index.js';
import { LayoutSize, LayoutStyles } from 'lib/ui/layout/layout-ui.service.js';
import { IImage } from 'lib/ui/ui-models.js';
import { map } from 'rxjs';
import { routingModulePaths } from '../app-routing-module-helper.js';
import { CoreAppComponent } from '../core/core-app.component.js';
import { environment } from '../environments/environment.js';
import { homeHelper } from '../pages/home/helpers/home-helper.js';
import { IPageMetadata, ISharedData, ITypeParentRoute } from '../pages/type-routes/type-route.models.js';
import { ILayoutMenuItem, SecondaryMenuItem } from './layout.models.js';
import {
	ExtendedSideMenuCategory,
	ExtendedSideMenuItem,
	PrimaryMenuType,
	SideMenuCategory,
	SideMenuItem
} from './menus/menu.models.js';
import { IMenuItemWithState } from './services/layout-service.js';

type PageLayoutDesign = 'auto' | 'clean' | 'home' | 'focused';
type CalculatedPageLayoutDesign = 'authPage' | 'cleanPage' | 'homePage' | 'focusedPage' | 'accountDisabled';

type FooterItem = {
	readonly format: 'title' | 'default' | 'small';
	readonly text: string;
	readonly routerLink?: string;
	readonly anchorRoute?: string;
};

type FooterSection = {
	readonly title: string | undefined;
	readonly items: readonly FooterItem[];
};

type FooterItems = {
	readonly sectionA: FooterSection;
	readonly sectionB: FooterSection;
	readonly sectionC: FooterSection;
};

@Component({
	selector: 'app-layout-main',
	templateUrl: './main-layout.component.html',
	styleUrls: ['./main-layout.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class MainLayoutComponent extends CoreAppComponent {
	public readonly sharedData = input<ISharedData<any>>();
	public readonly menuItems = input<readonly IMenuItemWithState[]>();
	public readonly pageMetadata = input<IPageMetadata>();
	public readonly parentRoutes = input<readonly ITypeParentRoute[]>();
	public readonly layoutSize = input<LayoutSize>();
	public readonly design = input<PageLayoutDesign>('auto');
	public readonly includeFooter = input<boolean>(true);

	public readonly sharedDataUpdated = output<ISharedData<any>>();

	public readonly newVersionTitle = computed<string | undefined>(() => {
		const versionState = this.appVersionState();
		if (!versionState) {
			return undefined;
		}
		return `New app version ${versionState.newVersion} available`;
	});

	public readonly newVersionText = computed<string | undefined>(() => {
		const versionState = this.appVersionState();
		if (!versionState) {
			return undefined;
		}
		return `Please update it by clicking on the button below. Your app version is ${versionState.currentVersion}.`;
	});

	public readonly pageTitle = computed<string | undefined>(() => {
		const pageMetadata = this.pageMetadata();
		if (pageMetadata?.card?.title?.value) {
			return pageMetadata.card.title.value;
		}
		return pageMetadata?.title;
	});

	public readonly primaryMenuType = computed<PrimaryMenuType>(() => {
		if (this.calculatedDesign() === 'authPage') {
			return 'authPage';
		}
		return 'default';
	});

	public readonly pageSubtitle = computed<string | undefined>(() => {
		const pageMetadata = this.pageMetadata();
		if (pageMetadata?.card?.subtitle) {
			return pageMetadata?.card?.subtitle;
		}
		return undefined;
	});

	public readonly pageAvatar = computed<IImage | undefined>(() => {
		const pageMetadata = this.pageMetadata();
		if (this.isMobileView()) {
			if (pageMetadata?.card?.avatar) {
				return pageMetadata?.card?.avatar.avatar;
			}
		}

		return undefined;
	});

	public readonly calculatedDesign = computed<CalculatedPageLayoutDesign>(() => {
		const authUser = this.authUser();

		if (authUser?.isDisabled) {
			return 'accountDisabled';
		}

		const design = this.design();

		if (design === 'home') {
			return 'homePage';
		}

		if (design === 'focused') {
			return 'focusedPage';
		}

		if (design === 'auto' || !design) {
			if (authUser) {
				return 'authPage';
			}
		}

		return 'cleanPage';
	});

	public readonly buttons = computed<readonly IButton[]>(() => {
		if (this.isMobileView()) {
			// this will be buttons in the toolbar - only in mobile
			return this.pageMetadata()?.card?.buttons ?? [];
		}

		return [];
	});

	public readonly layoutSizeStyles = computed<LayoutStyles>(() => {
		const calculatedDesign = this.calculatedDesign();

		if (calculatedDesign === 'homePage' || calculatedDesign === 'cleanPage') {
			return this.dependencies.layoutUiService.getLayoutSizeStyles({
				screenService: this.dependencies.screenService,
				type: 'large'
			});
		}
		return {};
	});

	public readonly footerItems = computed<FooterItems>(() => {
		return {
			sectionA: {
				title: 'About',
				items: [
					{
						format: 'default',
						text: `Developed in EU`
					},
					{
						format: 'default',
						text: `All rights reserved`
					},
					{
						format: 'default',
						text: `© ${this.dependencies.timeService.now().year} ${this.originalAppName()}`
					},
					{
						format: 'small',
						text: `v${this.appContext()?.appSetup?.appVersion}`
					}
				]
			},
			sectionB: {
				title: 'Company',
				items: [
					{
						format: 'default',
						text: `FAQs`,
						anchorRoute: homeHelper.homeAnchors.faqs
					},
					{
						format: 'default',
						text: `Privacy`,
						routerLink: routingModulePaths.privacy
					},
					{
						format: 'default',
						text: `Cookie Policy`,
						routerLink: routingModulePaths.cookies
					},
					{
						format: 'default',
						text: `Refund Policy`,
						routerLink: routingModulePaths.refundPolicy
					},
					{
						format: 'default',
						text: `Terms & Conditions`,
						routerLink: routingModulePaths.terms
					},
					{
						format: 'default',
						text: `Pricing`,
						anchorRoute: homeHelper.homeAnchors.pricing
					}
				]
			},
			sectionC: {
				title: 'Contact',
				items: [
					{
						format: 'default',
						text: `We'll be happy to assist you on the e-mail below:`
					},
					{
						format: 'default',
						text: environment.contactEmailShownInFooter
					}
				]
			}
		};
	});

	public readonly toolbarMenuItems = computed<ILayoutMenuItem[]>(() => {
		const items: ILayoutMenuItem[] = [];

		if (this.calculatedDesign() === 'cleanPage' || this.calculatedDesign() === 'homePage') {
			items.push(
				{
					title: 'Pricing',
					anchorRoute: '#' + homeHelper.homeAnchors.pricing,
					icon: 'purchase'
				},
				{
					title: 'Features',
					anchorRoute: '#' + homeHelper.homeAnchors.features,
					icon: 'features'
				},
				{
					title: 'Contact',
					anchorRoute: '#' + homeHelper.homeAnchors.contact,
					icon: 'comment'
				},
				{
					title: 'FAQs',
					anchorRoute: '#' + homeHelper.homeAnchors.faqs,
					icon: 'faq'
				},
				{
					title: 'App',
					anchorRoute: '#' + homeHelper.homeAnchors.app,
					icon: 'download'
				}
			);
		}

		return items;
	});

	private readonly sideCategories = computed<readonly SideMenuCategory[]>(() => {
		let categories: SideMenuCategory[] = [];
		const toolbarMenuItems = this.toolbarMenuItems() ?? [];
		const authUser = this.authUser();

		const unreadFeedsCount = this.unreadFeedsCount();

		// only show notification item on mobile view
		const notificationItem: SideMenuItem | undefined = this.isMobileView()
			? {
					title: 'Notifications',
					iconType: 'notification',
					activeForRoutes: [],
					activeForUrls: [
						super.actionRoute((routes) => routes.profileRoutes.view.children.notifications).getUrl()
					],
					routerLink: super
						.actionRoute((routes) => routes.profileRoutes.view.children.notifications)
						.getUrl(),
					badge: unreadFeedsCount ? `${unreadFeedsCount}` : undefined
			  }
			: undefined;

		const clientChatItem: SideMenuItem | undefined = authUser
			? {
					title: `Chat`,
					iconType: 'comment',
					routerLink: super
						.actionRoute((routes) => routes.chatRoutes.conversation.children.view, authUser.trainerId)
						.getUrl(),
					activeForRoutes: [this.dependencies.navigateService.appRoutes.chatRoutes.conversation]
			  }
			: undefined;

		const trainerChatItem: SideMenuItem | undefined = authUser
			? {
					title: `Chat`,
					iconType: 'comment',
					routerLink: super.actionRoute((routes) => routes.chatRoutes.overview.children.list).getUrl(),
					activeForRoutes: [
						this.dependencies.navigateService.appRoutes.chatRoutes.overview,
						this.dependencies.navigateService.appRoutes.chatRoutes.conversation
					]
			  }
			: undefined;

		if (!authUser) {
			return [
				{
					id: '_connect',
					title: 'Get Started',
					icon: 'rocket',
					items: [
						{
							title: 'Sign up',
							iconType: 'rocket',
							activeForRoutes: [],
							activeForUrls: [this.dependencies.navigateService.signupPage().getUrl()],
							routerLink: this.dependencies.navigateService.signupPage().getUrl()
						},
						{
							title: 'Login',
							iconType: 'login',
							activeForRoutes: [],
							activeForUrls: [this.dependencies.navigateService.loginPage().getUrl()],
							routerLink: this.dependencies.navigateService.loginPage().getUrl()
						}
					]
				},
				{
					icon: 'branding',
					id: '_default',
					title: 'Company',
					items: toolbarMenuItems.map((m) => {
						const activeForRoutes: string[] = [];

						if (m.anchorRoute) {
							activeForRoutes.push(m.anchorRoute);
						}
						if (m.routerLink) {
							activeForRoutes.push(m.routerLink);
						}
						const item: SideMenuItem = {
							title: m.title,
							anchorRoute: m.anchorRoute,
							routerLink: m.routerLink,
							externalUrl: m.externalUrl,
							iconType: m.icon ?? 'rocket',
							activeForUrls: activeForRoutes,
							activeForRoutes: []
						};

						return item;
					})
				}
			];
		}

		if (authUser.userType === 'trainer') {
			categories = [
				{
					icon: 'dashboard',
					id: 'trainerHome',
					title: 'Home',
					items: [
						{
							title: `Dashboard`,
							iconType: 'dashboard',
							routerLink: super
								.actionRoute((routes) => routes.trainerHomeRoutes.dashboard.children.main)
								.getUrl(),
							activeForRoutes: [this.dependencies.navigateService.appRoutes.trainerHomeRoutes.dashboard]
						},
						...(notificationItem ? [notificationItem] : [])
					]
				},
				{
					icon: 'users',
					id: 'clients',
					title: 'Clients',
					items: [
						{
							title: `Clients`,
							iconType: 'users',
							routerLink: super
								.actionRoute((routes) => routes.trainerClientRoutes.clients.children.list)
								.getUrl(),
							activeForRoutes: [
								this.dependencies.navigateService.appRoutes.trainerClientRoutes.client,
								this.dependencies.navigateService.appRoutes.trainerClientRoutes.clients,
								this.dependencies.navigateService.appRoutes.trainerClientRoutes['client-workout']
							]
						},
						...(this.isMobileView() && trainerChatItem ? [trainerChatItem] : [])
					]
				},
				{
					icon: 'comment',
					id: 'chat',
					title: 'Chat',
					hide: this.isMobileView(),
					items: [...(trainerChatItem ? [trainerChatItem] : [])]
				},
				{
					icon: 'dumbbell',
					id: `trainerTraining`,
					title: `Training`,
					items: [
						{
							title: `Workout templates`,
							iconType: 'workout',
							routerLink: super
								.actionRoute((routes) => routes.workoutRoutes.workouts.children.custom)
								.getUrl(),
							activeForRoutes: [
								this.dependencies.navigateService.appRoutes.workoutRoutes.workout,
								this.dependencies.navigateService.appRoutes.workoutRoutes.workouts
							]
						},
						{
							title: `Activities`,
							iconType: 'workoutActivity',
							routerLink: super
								.actionRoute((routes) => routes.workoutRoutes.activities.children.custom)
								.getUrl(),
							activeForRoutes: [
								this.dependencies.navigateService.appRoutes.workoutRoutes.activity,
								this.dependencies.navigateService.appRoutes.workoutRoutes.activities
							]
						},
						{
							title: `My exercises`,
							iconType: 'exercises',
							routerLink: super
								.actionRoute((routes) => routes.exerciseRoutes.exercises.children.custom)
								.getUrl(),
							activeForRoutes: [
								this.dependencies.navigateService.appRoutes.exerciseRoutes.exercise,
								this.dependencies.navigateService.appRoutes.exerciseRoutes.exercises
							]
						},
						{
							title: `Exercise database`,
							iconType: 'exercises',
							routerLink: super
								.actionRoute((routes) => routes.exerciseRoutes.globalExercises.children.list)
								.getUrl(),
							activeForRoutes: [
								this.dependencies.navigateService.appRoutes.exerciseRoutes.globalExercises
							]
						}
					]
				},
				{
					icon: 'food',
					id: 'trainerNutrition',
					title: `Nutrition`,
					items: [
						{
							title: `Diet templates`,
							iconType: 'diet',
							routerLink: super.actionRoute((routes) => routes.dietRoutes.diets.children.custom).getUrl(),
							activeForRoutes: [
								this.dependencies.navigateService.appRoutes.dietRoutes.diet,
								this.dependencies.navigateService.appRoutes.dietRoutes.diets
							]
						},
						{
							title: `My foods`,
							iconType: 'foods',
							routerLink: super.actionRoute((routes) => routes.foodRoutes.foods.children.custom).getUrl(),
							activeForRoutes: [
								this.dependencies.navigateService.appRoutes.foodRoutes.food,
								this.dependencies.navigateService.appRoutes.foodRoutes.foods
							]
						},
						{
							title: `Food database`,
							iconType: 'foods',
							routerLink: super
								.actionRoute((routes) => routes.foodRoutes.globalFoods.children.list)
								.getUrl(),
							activeForRoutes: [this.dependencies.navigateService.appRoutes.foodRoutes.globalFoods]
						}
					]
				},
				{
					icon: 'analytics',
					id: 'tracking',
					title: `Tracking`,
					items: [
						{
							title: `Progress types`,
							iconType: 'analytics',
							routerLink: super
								.actionRoute((routes) => routes.progressRoutes.progressTypes.children.custom)
								.getUrl(),
							activeForRoutes: [
								this.dependencies.navigateService.appRoutes.progressRoutes.progressType,
								this.dependencies.navigateService.appRoutes.progressRoutes.progressTypes
							]
						},
						{
							title: `Tasks`,
							iconType: 'task',
							routerLink: super.actionRoute((routes) => routes.taskRoutes.tasks.children.custom).getUrl(),
							activeForRoutes: [
								this.dependencies.navigateService.appRoutes.taskRoutes.task,
								this.dependencies.navigateService.appRoutes.taskRoutes.tasks
							]
						}
					]
				},
				{
					icon: 'calendar',
					id: 'trainerCalendar',
					title: `Calendar`,
					items: [
						{
							title: `Upcoming appointments`,
							iconType: 'calendar',
							routerLink: super
								.actionRoute((routes) => routes.calendarRoutes.view.children.upcoming)
								.getUrl(),
							activeForRoutes: [],
							activeForUrls: [
								this.dependencies.navigateService
									.actionRoute((routes) => routes.calendarRoutes.view.children.upcoming)
									.getUrl()
							]
						},
						{
							title: `Past appointments`,
							iconType: 'date',
							routerLink: super
								.actionRoute((routes) => routes.calendarRoutes.view.children.past)
								.getUrl(),
							activeForRoutes: [],
							activeForUrls: [
								this.dependencies.navigateService
									.actionRoute((routes) => routes.calendarRoutes.view.children.past)
									.getUrl()
							]
						}
					]
				},
				{
					id: 'Branding',
					title: `Branding`,
					icon: 'branding',
					items: [
						{
							iconType: 'branding',
							title: `Branding`,
							routerLink: super
								.actionRoute((routes) => routes.brandingRoutes.form.children.main)
								.getUrl(),
							activeForRoutes: [this.dependencies.navigateService.appRoutes.brandingRoutes.form]
						}
					]
				},
				{
					id: 'Packages',
					title: `Packages`,
					icon: 'purchase',
					items: [
						{
							title: `Packages`,
							iconType: 'services',
							routerLink: super
								.actionRoute((routes) => routes.packageRoutes.packages.children.custom)
								.getUrl(),
							activeForRoutes: [
								this.dependencies.navigateService.appRoutes.packageRoutes.package,
								this.dependencies.navigateService.appRoutes.packageRoutes.packages
							]
						}
					]
				},
				{
					id: 'AiChat',
					title: `AI Chat`,
					icon: 'genai',
					items: [
						{
							title: `AI Chat`,
							iconType: 'genai',
							routerLink: super.actionRoute((routes) => routes.genaiRoutes.genai.children.chat).getUrl(),
							activeForRoutes: [this.dependencies.navigateService.appRoutes.genaiRoutes.genai]
						}
					]
				}
			];
		}

		if (authUser.userType === 'client') {
			categories.push(
				{
					icon: 'dashboard',
					id: 'clientHome',
					title: 'Home',
					items: [
						{
							title: `Dashboard`,
							iconType: 'dashboard',
							routerLink: super
								.actionRoute((routes) => routes.clientRoutes.dashboard.children.overview)
								.getUrl(),
							activeForRoutes: [this.dependencies.navigateService.appRoutes.clientRoutes.dashboard]
						},
						...(notificationItem ? [notificationItem] : []),
						...(this.isMobileView() && clientChatItem ? [clientChatItem] : [])
					]
				},
				{
					icon: 'comment',
					id: 'chat',
					title: 'Chat',
					hide: this.isMobileView(),
					items: [
						{
							title: `Chat`,
							iconType: 'comment',
							routerLink: super
								.actionRoute(
									(routes) => routes.chatRoutes.conversation.children.view,
									authUser.trainerId
								)
								.getUrl(),
							activeForRoutes: [this.dependencies.navigateService.appRoutes.chatRoutes.conversation]
						}
					]
				},
				{
					icon: 'dumbbell',
					id: `clientWorkout`,
					title: this.isMobileView() ? `Coaching` : 'Training',
					hideTitle: false,
					items: [
						{
							title: `Training plan`,
							iconType: 'workout',
							routerLink: super
								.actionRoute((routes) => routes.clientRoutes.training.children.overview)
								.getUrl(),
							activeForRoutes: [
								this.dependencies.navigateService.appRoutes.clientRoutes.training,
								this.dependencies.navigateService.appRoutes.clientRoutes.workout
							]
						}
					]
				},
				{
					icon: 'food',
					id: `clientNutrition`,
					title: `Nutrition`,
					hideTitle: this.isMobileView(),
					items: [
						{
							title: `Diet plan`,
							iconType: 'food',
							routerLink: super
								.actionRoute((routes) => routes.clientRoutes.nutrition.children.overview)
								.getUrl(),
							activeForRoutes: [
								this.dependencies.navigateService.appRoutes.clientRoutes.nutrition,
								this.dependencies.navigateService.appRoutes.clientRoutes.diet
							]
						}
					]
				},
				{
					icon: 'files',
					id: `clientFiles`,
					title: `Files`,
					hideTitle: this.isMobileView(),
					items: [
						{
							title: `Assigned files`,
							iconType: 'files',
							routerLink: super
								.actionRoute((routes) => routes.clientRoutes.files.children.overview)
								.getUrl(),
							activeForRoutes: [this.dependencies.navigateService.appRoutes.clientRoutes.files]
						}
					]
				},
				{
					icon: 'gallery',
					id: `clientGallery`,
					title: `Gallery`,
					hideTitle: this.isMobileView(),
					items: [
						{
							title: `View gallery`,
							iconType: 'gallery',
							routerLink: super
								.actionRoute((routes) => routes.clientRoutes.gallery.children.overview)
								.getUrl(),
							activeForRoutes: [this.dependencies.navigateService.appRoutes.clientRoutes.gallery]
						}
					]
				},

				{
					icon: 'analytics',
					id: `clientSessions`,
					title: `Sessions`,
					hideTitle: this.isMobileView(),
					items: [
						{
							title: `Sessions`,
							iconType: 'relativeTime',
							routerLink: super
								.actionRoute((routes) => routes.clientRoutes.sessions.children.manage)
								.getUrl(),
							activeForRoutes: [this.dependencies.navigateService.appRoutes.clientRoutes.sessions]
						}
					]
				},
				{
					icon: 'target',
					id: `clientProgress`,
					title: `Progress`,
					hideTitle: this.isMobileView(),
					items: [
						{
							title: `Progress`,
							iconType: 'analytics',
							routerLink: super
								.actionRoute((routes) => routes.clientRoutes.progress.children.overview)
								.getUrl(),
							activeForRoutes: [this.dependencies.navigateService.appRoutes.clientRoutes.progress]
						}
					]
				},
				{
					icon: 'task',
					id: `clientTasks`,
					title: `Tasks`,
					hideTitle: this.isMobileView(),
					items: [
						{
							title: `Tasks`,
							iconType: 'task',
							routerLink: super
								.actionRoute((routes) => routes.clientRoutes.tasks.children.overview)
								.getUrl(),
							activeForRoutes: [this.dependencies.navigateService.appRoutes.clientRoutes.tasks]
						}
					]
				},
				{
					icon: 'calendar',
					id: `clientCalendar`,
					title: `Calendar`,
					items: [
						{
							title: `Upcoming appointments`,
							iconType: 'calendar',
							routerLink: super
								.actionRoute((routes) => routes.clientRoutes.calendar.children.upcoming)
								.getUrl(),
							activeForRoutes: [],
							activeForUrls: [
								this.dependencies.navigateService
									.actionRoute((routes) => routes.clientRoutes.calendar.children.upcoming)
									.getUrl()
							]
						},
						{
							title: `Past appointments`,
							iconType: 'date',
							routerLink: super
								.actionRoute((routes) => routes.clientRoutes.calendar.children.past)
								.getUrl(),
							activeForRoutes: [],
							activeForUrls: [
								this.dependencies.navigateService
									.actionRoute((routes) => routes.clientRoutes.calendar.children.past)
									.getUrl()
							]
						}
					]
				}
			);
		}

		if (authUser.privilege === 'admin') {
			categories.push({
				icon: 'admin',
				id: 'admin',
				title: `Admin`,
				items: [
					{
						title: `Logs`,
						iconType: 'log',
						routerLink: super
							.actionRoute((routes) => routes.adminLogRoutes.logs.children.listLogs)
							.getUrl(),
						activeForRoutes: [this.dependencies.navigateService.appRoutes.adminLogRoutes.logs]
					},
					{
						title: `Emails`,
						iconType: 'email',
						routerLink: super
							.actionRoute((routes) => routes.adminLogRoutes.emails.children.listEmails)
							.getUrl(),
						activeForRoutes: [this.dependencies.navigateService.appRoutes.adminLogRoutes.emails]
					},
					{
						title: `Users`,
						iconType: 'users',
						routerLink: super.actionRoute((routes) => routes.adminUserRoutes.users.children.list).getUrl(),
						activeForRoutes: [this.dependencies.navigateService.appRoutes.adminUserRoutes.users]
					},
					{
						title: `Analytics`,
						iconType: 'analytics',
						routerLink: super
							.actionRoute((routes) => routes.adminAnalyticsRoutes.analytics.children.view)
							.getUrl(),
						activeForRoutes: [this.dependencies.navigateService.appRoutes.adminAnalyticsRoutes.analytics]
					},
					{
						title: `Helpdesk`,
						iconType: 'helpdesk',
						routerLink: super
							.actionRoute((routes) => routes.helpdeskRoutes.view.children.conversations)
							.getUrl(),
						activeForRoutes: [this.dependencies.navigateService.appRoutes.helpdeskRoutes.view]
					},
					{
						title: `App setup`,
						iconType: 'settings',
						routerLink: super
							.actionRoute((routes) => routes.adminAppSetupRoutes.appSetup.children.form)
							.getUrl(),
						activeForRoutes: [this.dependencies.navigateService.appRoutes.adminAppSetupRoutes.appSetup]
					},
					{
						title: `Image gallery`,
						iconType: 'gallery',
						routerLink: super
							.actionRoute((routes) => routes.adminImageGalleryRoutes.imageGallery.children.form)
							.getUrl(),
						activeForRoutes: [
							this.dependencies.navigateService.appRoutes.adminImageGalleryRoutes.imageGallery
						]
					},
					{
						title: `Background tasks`,
						iconType: 'log',
						routerLink: super
							.actionRoute((routes) => routes.adminLogRoutes.backgroundTaskLogs.children.overview)
							.getUrl(),
						activeForRoutes: [this.dependencies.navigateService.appRoutes.adminLogRoutes.backgroundTaskLogs]
					}
				]
			});
		}

		if (this.isMobileView()) {
			categories.push({
				icon: 'theme',
				id: 'ThemeMobile',
				title: 'Theme',
				items: [],
				tags: [
					{
						text: 'Light',
						tagType: this.theme() === 'light' ? 'primary' : 'sideMenuBasic',
						click: () => {
							this.dependencies.themeService.setTheme('light');
						}
					},
					{
						text: 'Dark',
						tagType: this.theme() === 'dark' ? 'primary' : 'sideMenuBasic',
						click: () => {
							this.dependencies.themeService.setTheme('dark');
						}
					}
				]
			});
			categories.push({
				icon: 'settings',
				id: 'PreferredUnitMobile',
				title: 'Preferred Unit',
				items: [],
				specialType: 'preferredUnitSwitcher'
			});
			categories.push({
				icon: 'settings',
				id: 'OtherMobile',
				title: 'Other',
				items: [
					{
						title: 'Install app',
						iconType: 'download_app',
						activeForRoutes: [],
						routerLink: super
							.actionRoute((routes) => routes.profileRoutes.utilities.children.install)
							.getUrl()
					},
					{
						title: `Profile`,
						iconType: 'user',
						routerLink: super.actionRoute((routes) => routes.profileRoutes.view.children.edit).getUrl(),
						activeForRoutes: [this.dependencies.navigateService.appRoutes.profileRoutes.view]
					},
					{
						title: 'Helpdesk',
						iconType: 'helpdesk',
						routerLink: this.dependencies.navigateService
							.actionRoute((routes) => routes.helpdeskRoutes.conversation.children.form, authUser.id)
							.getUrl(),
						activeForRoutes: []
					},
					{
						title: 'Log out',
						iconType: 'logout',
						routerLink: this.dependencies.navigateService.logoutPage().getUrl(),
						activeForRoutes: []
					}
				]
			});
		}

		const filteredCategories: SideMenuCategory[] = [];

		for (const category of categories) {
			if (category.hide === true) {
				// do nothing
			} else {
				filteredCategories.push({
					...category,
					items: category.items.filter((m) => m.hide !== true)
				});
			}
		}

		return filteredCategories;
	});

	private readonly currentUrl = signal<string>(this.dependencies.router.url);

	public readonly extendedSideMenuCategories = computed<ExtendedSideMenuCategory[]>(() => {
		const url: string = this.currentUrl()?.toLowerCase() ?? '';

		return this.sideCategories().map((sideCategory) => {
			const extendedSideCategory: ExtendedSideMenuCategory = {
				...sideCategory,
				externalUrl: sideCategory.items?.[0]?.externalUrl,
				routerLink: sideCategory.items?.[0]?.routerLink,
				isActive: sideCategory.items.some((item) => this.isSideItemActive(url, item)),
				items: sideCategory.items.map<ExtendedSideMenuItem>((categoryItem) => {
					return {
						...categoryItem,
						isActive: this.isSideItemActive(url, categoryItem)
					};
				})
			};

			return extendedSideCategory;
		});
	});

	public readonly activeSideMenuCategory = computed<ExtendedSideMenuCategory | undefined>(() => {
		return this.extendedSideMenuCategories().find((category) => category.items.some((item) => item.isActive));
	});

	public readonly secondaryMenuItems = computed<SecondaryMenuItem[]>(() => {
		const menuItems = this.menuItems() ?? [];

		if (menuItems.length) {
			return menuItems.map((m) => {
				return {
					id: `${m.title}-${m.routerLink}`,
					isActive: m.isActive,
					title: m.title,
					routerLink: m.routerLink,
					iconType: m.iconType
				};
			});
		}

		const activeSideMenuCategory = this.activeSideMenuCategory();
		const url: string = this.currentUrl()?.toLowerCase() ?? '';

		return (
			activeSideMenuCategory?.items.map((item) => {
				return {
					id: `${item.title}-${item.routerLink}`,
					isActive: this.isSideItemActive(url, item),
					routerLink: item.routerLink,
					title: item.title,
					externalUrl: item.externalUrl,
					iconType: item.iconType
				};
			}) ?? []
		);
	});

	constructor(private title: Title) {
		super();

		effect(() => {
			const title = this.pageTitle();

			if (title) {
				this.updatePageTitle(title);
			}
		});
	}

	ngOnInit(): void {
		super.subscribeToObservable(
			this.dependencies.router.events.pipe(
				map((data) => {
					if (data instanceof NavigationEnd) {
						this.currentUrl.set(data.url);
					}
				})
			)
		);
	}

	handleUpdateApp(): void {
		if (!this.isBrowserPlatform()) {
			return;
		}
		reloadHelper.handleUpdateApp();
	}

	private updatePageTitle(title: string): void {
		this.title.setTitle(title);
	}

	private isSideItemActive(url: string, sideItem: SideMenuItem): boolean {
		for (const typeRoute of sideItem.activeForRoutes) {
			const urlPath = this.dependencies.navigateService.typeRoute((routes) => typeRoute);
			if (url.includes(`/${typeRoute.mainPath}/${urlPath}/`.toLowerCase())) {
				return true;
			}
		}

		if (sideItem.activeForUrls) {
			for (const activeForUrl of sideItem.activeForUrls) {
				if (this.dependencies.router.url.toLowerCase().includes(activeForUrl.toLowerCase())) {
					return true;
				}
			}
		}

		return false;
	}
}
